import { createFetch } from "@vueuse/core";
import { ref } from "vue";
import {
  generateQueryString,
  generateUrl,
  parseStringify,
  goToRoute,
  sleep,
} from "./utils";
import Notify from "@/shared/toast";
import { includes, pathOr } from "ramda";
import axios from "axios";
axios.defaults.withCredentials = true;

interface FetchMiddleWare {
  url: string;
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  afterFetch?: (ctx: any) => any;
  beforeFetch?: (ctx: any) => any;
  onFetchError?: (ctx: any) => any;
  immediate?: boolean;
  responseType?: string;
  createNewInstance?: boolean;
}

export interface ApiHooks {
  afterFetch?: any;
  beforeFetch?: any;
  onFetchError?: any;
  params?: any;
  id?: string | string[] | null;
}
let isAccessibility: any = true;
let isRefreshTokenInProgress: any;
export const getRefreshToken = async () => {
  const doLogout = async () => {
    localStorage.clear();
    indexedDB.deleteDatabase("imagesCached");
    goToRoute("auth", {}, {}, false);
    await sleep(500);
    location.reload();
  };

  if (!isAccessibility) {
    doLogout();
    return;
  }

  try {
    axios.defaults.withCredentials = true;
    const url = import.meta.env.VUE_APP_API_URL + "v1/refresh-cookie-token";
    const response = await axios.post(url);
    return response;
  } catch (error) {
    if (error) {
      const statusCode = pathOr(401, ["response", "status"], error);
      if (includes(statusCode, [401, 412])) {
        doLogout();
        return false;
      }
    }
  }
};
const localLang = localStorage.getItem("localLang");
const envLang = import.meta.env.VUE_APP_I18N_LOCALE;
const locale = localLang || envLang || "fa";

// for get services
const useMyFetch = createFetch({
  baseUrl: import.meta.env.VUE_APP_API_URL,
  combination: "chain",
  options: {
    async beforeFetch({ options, url, cancel }) {
      options.headers = {
        Accept: "application/json",
        "Cache-Control": "public, max-age=3600",
      };
      options.credentials = "include";
      if (options.method !== "GET" && options.body) {
        if (options.body instanceof FormData) {
          const formData = new FormData();
          const body = pathOr(formData, ["body"], options);
          const replacement: any = body.get("replacement");
          const queryString: any = body.get("queryString");

          if (replacement) {
            const parsedReplacement = JSON.parse(replacement);
            for (const key in parsedReplacement) {
              if (
                Object.prototype.hasOwnProperty.call(parsedReplacement, key)
              ) {
                // replace values
                url = url?.replace(`{${key}}`, parsedReplacement[key]);
              }
            }
          }
          if (queryString) {
            const parsedQueryString = JSON.parse(queryString);
            url = generateQueryString(url, parsedQueryString);
          }

          body.delete("replacement");
          body.delete("queryString");
          options.body = body;
        } else {
          const bodyObj = parseStringify(options.body);

          if (bodyObj.replacement) {
            const replacement = bodyObj.replacement;
            for (const key in replacement) {
              if (Object.prototype.hasOwnProperty.call(replacement, key)) {
                // replace values
                url = url?.replace(`{${key}}`, replacement[key]);
              }
            }
          }
          if (bodyObj.queryString) {
            url = generateQueryString(url, bodyObj.queryString);
          }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { replacement, queryString, ...payload } = bodyObj;

          options.body = payload;
        }
        return { options, url, cancel };
      }
    },
    afterFetch(ctx) {
      if (typeof ctx.data === "string") {
        const data = JSON.parse(ctx.data);
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { data: ignoredData, ...configs } = data;

        const lastData = Array.isArray(data.data)
          ? { data: data.data }
          : data.data;
        return {
          data: { ...lastData, configs },
          message: data.message,
          configs,
        };
      } else if (ctx.data instanceof Blob) {
        return { data: ctx.data };
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { data: ignoredData, ...configs } = ctx.data;
      return {
        data: ctx.data.data,
        message: ctx.data.message,
        configs,
      };
    },
    onFetchError(ctx) {
      /* start log error for debugging */
      if (import.meta.env.DEV) {
        const data = JSON.parse(ctx.data);
        console.log(`
          =================================
          ${"URL"}: ${ctx.response?.url}
          STATUS: ${ctx.response?.status}
          MESSAGE: ${data?.message}
  
          PAYLOAD: 
          =================================
          `);
      }
      /* end log error for debugging */
      const statusCode = ctx?.response?.status;
      delete ctx.error;

      const parsedData = JSON.parse(ctx.data);
      isAccessibility = pathOr(true, ["accessibility"], parsedData);

      if (
        statusCode !== 200 &&
        statusCode !== 401 &&
        typeof ctx.data === "string"
      ) {
        const data = JSON.parse(ctx.data);
        const message = data.message;
        if (Array.isArray(message)) {
          message.forEach((message: any) => Notify.error(message));
        } else {
          Notify.error(message);
        }
        return {
          data,
          response: ctx.response,
        };
      }
      return {
        data: ctx.data,
        response: ctx.response,
      };
    },
  },
});

// for not get services
const createApi = (
  url: string,
  method: string,
  hooks: ApiHooks,
  immediate: boolean,
  responseType: string,
  createNewInstance: boolean
) => {
  // نمونه اصلی api
  let api: any = useMyFetch(
    url,
    { method },
    { ...hooks, updateDataOnError: true, immediate }
  );
  if (responseType === "blob") {
    api = api.blob();
  }

  const createNewApiInstance = () => {
    let newApi: any = useMyFetch(
      url,
      { method },
      { ...hooks, updateDataOnError: true, immediate }
    );
    if (responseType === "blob") {
      newApi = newApi.blob();
    }
    return newApi;
  };

  const mutate = async (body: any) => {
    // تنظیم null برای کلیدها زمانی که مقدار undefined است
    for (const [key, value] of Object.entries(body)) {
      if (value === undefined) {
        body[key] = null;
      }
    }

    const dataType: any = pathOr("JSON", ["dataType"], body);

    // استفاده از یک نمونه جدید api
    const localApi = createNewInstance ? createNewApiInstance() : api;

    if (dataType === "FORMDATA") {
      delete body.dataType;
      const formData: any = new FormData();
      Object.entries(body).forEach(([key, value]) => {
        if (value) formData.append(key, value);
      });
      localApi[method.toLowerCase()](formData);
    } else {
      delete body.dataType;
      localApi[method.toLowerCase()](body);
    }

    return localApi.execute().then(async (response: any) => {
      if (response && localApi.statusCode.value === 200) {
        return {
          response,
          data: localApi.data.value,
        };
      }
      if (localApi.statusCode.value === 401) {
        if (!isRefreshTokenInProgress) {
          isRefreshTokenInProgress = getRefreshToken().then((res) => {
            isRefreshTokenInProgress = null; // پاکسازی وضعیت
            return res; // بازگرداندن توکن جدید
          });
        }
        return isRefreshTokenInProgress.then(async () => {
          const data: any = await mutate(body);
          return data;
        });
      }
      const message = localApi.data.value;
      throw { data: message };
    });
  };

  // بازگرداندن نمونه اصلی api و تابع mutate
  return { ...api, mutate };
};

const fetchMiddleWare = ({
  url,
  method,
  afterFetch = (ctx: any) => ctx,
  beforeFetch = (ctx: any) => ctx,
  onFetchError = (ctx: any) => ctx,
  immediate = true,
  responseType = "json",
  createNewInstance = false,
}: FetchMiddleWare) => {
  if (
    method === "POST" ||
    method === "PUT" ||
    method === "PATCH" ||
    method === "DELETE"
  ) {
    const onFetchErrorMain = (ctx: any) => {
      if (ctx?.response?.status === 401 && immediate) {
        if (!isRefreshTokenInProgress) {
          isRefreshTokenInProgress = getRefreshToken().then((res) => {
            isRefreshTokenInProgress = null; // پاکسازی وضعیت
            return res; // بازگرداندن توکن جدید
          });
        }
        return isRefreshTokenInProgress.then(async () => {
          await api.mutate({});
        });
      }

      return onFetchError(ctx);
    };

    const api = createApi(
      url,
      method,
      { afterFetch, beforeFetch, onFetchError: onFetchErrorMain },
      false,
      responseType,
      createNewInstance
    );
    return api;
  } else {
    const replacement = ref([]);
    const queryString = ref(null);
    const extraQueryString = ref("");
    const fullQueryString = ref(null);
    const mutateUrl = ref(null);

    const onFetchErrorMain = (ctx: any) => {
      if (ctx?.response?.status === 401 && immediate) {
        if (!isRefreshTokenInProgress) {
          isRefreshTokenInProgress = getRefreshToken().then((res) => {
            isRefreshTokenInProgress = null; // پاکسازی وضعیت
            return res; // بازگرداندن توکن جدید
          });
        }
        return isRefreshTokenInProgress.then(async () => {
          await mutate({});
        });
      }
      return onFetchError(ctx);
    };

    const beforeGetFetch = (ctx: any) => {
      ctx.url =
        mutateUrl.value ??
        generateUrl(
          ctx.url,
          replacement.value,
          queryString.value,
          extraQueryString.value
        );

      if (fullQueryString.value) {
        ctx.url = ctx.url + fullQueryString.value;
      }

      // set language
      ctx.url = ctx.url.replace("/fa/", `/${locale}/`);

      return ctx;
    };

    const mutate = async (body: any) => {
      fullQueryString.value = body?.fullQueryString;
      extraQueryString.value = body?.extraQueryString;
      replacement.value = body?.replacement;
      queryString.value = body?.queryString;
      mutateUrl.value = body?.url;

      // ایجاد یک نمونه جدید از api
      let localApi = createNewInstance
        ? useMyFetch(
            url,
            { method },
            {
              afterFetch,
              beforeFetch: beforeGetFetch,
              onFetchError: onFetchErrorMain,
              immediate,
            }
          )
        : api;
      if (responseType === "blob") {
        localApi = localApi.blob();
      }

      return localApi.execute().then(async (response: any) => {
        if (response && localApi.statusCode.value === 200) {
          return {
            response,
            data: localApi.data.value,
          };
        }
        if (localApi.statusCode.value === 401) {
          if (!isRefreshTokenInProgress) {
            isRefreshTokenInProgress = getRefreshToken().then((res) => {
              isRefreshTokenInProgress = null; // پاکسازی وضعیت
              return res; // بازگرداندن توکن جدید
            });
          }
          return isRefreshTokenInProgress.then(async () => {
            const data: any = await mutate(body);
            return data;
          });
        }
        const message = localApi.data.value;
        throw { data: message };
      });
    };

    // ایجاد نمونه اصلی api
    let api = useMyFetch(
      url,
      { method },
      {
        afterFetch,
        beforeFetch: beforeGetFetch,
        onFetchError: onFetchErrorMain,
        immediate,
      }
    );
    if (responseType === "blob") {
      api = api.blob();
    }

    return { ...api, mutate };
  }
};

export default fetchMiddleWare;
